import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FunnelSimple, X, Plus, FloppyDisk, ArrowCounterClockwise, DotsSixVertical } from "@phosphor-icons/react";

export default function AdminFilters() {
  const [groups, setGroups] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const r = await api.get("/admin/filter-options");
    setGroups(r.data);
    const d = {};
    r.data.forEach((g) => { d[g.group] = g.values.slice(); });
    setDrafts(d);
  };
  useEffect(() => { load(); }, []);

  const setValue = (group, idx, val) => {
    const copy = drafts[group].slice();
    copy[idx] = val;
    setDrafts({ ...drafts, [group]: copy });
  };
  const removeValue = (group, idx) => {
    const copy = drafts[group].slice();
    copy.splice(idx, 1);
    setDrafts({ ...drafts, [group]: copy });
  };
  const addValue = (group) => setDrafts({ ...drafts, [group]: [...drafts[group], ""] });
  const moveValue = (group, from, to) => {
    if (to < 0 || to >= drafts[group].length) return;
    const copy = drafts[group].slice();
    const [v] = copy.splice(from, 1);
    copy.splice(to, 0, v);
    setDrafts({ ...drafts, [group]: copy });
  };
  const isDirty = (group) => {
    const orig = (groups.find((g) => g.group === group) || {}).values || [];
    const cur = drafts[group] || [];
    return orig.length !== cur.length || orig.some((v, i) => v !== cur[i]);
  };
  const revert = (group) => {
    const orig = (groups.find((g) => g.group === group) || {}).values || [];
    setDrafts({ ...drafts, [group]: orig.slice() });
  };
  const save = async (group) => {
    setBusy(group);
    try {
      const cleaned = (drafts[group] || []).map((v) => v.trim()).filter(Boolean);
      if (cleaned.length === 0) { toast.error("At least one value required"); return; }
      const r = await api.put(`/admin/filter-options/${group}`, { values: cleaned });
      toast.success(`${group.replace(/_/g, " ")} saved · ${r.data.values.length} values`);
      // Sync both stores so isDirty resets to false
      setGroups((prev) => prev.map((g) => g.group === group ? { ...g, values: r.data.values } : g));
      setDrafts({ ...drafts, [group]: r.data.values });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Admin · Portal Configuration</div>
        <h1 className="font-display text-4xl font-bold tracking-tight flex items-center gap-3">
          <FunnelSimple size={28} weight="fill" className="text-emerald-600" />
          Sidebar filters
        </h1>
        <p className="mt-2 text-muted-foreground text-sm max-w-2xl">
          Edit the value lists shown in the deal-flow sidebar filters. Changes take effect immediately for
          new page loads. Existing companies keep whatever value they already have — removing a value here
          does <b>not</b> retroactively re-tag any company.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {groups.map((g) => (
          <div key={g.group} className="rounded-2xl border border-border bg-card p-5" data-testid={`filter-group-${g.group}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-display font-bold text-lg">{g.label}</div>
                <div className="text-xs text-muted-foreground">{(drafts[g.group] || []).length} values · shown in the deal-flow sidebar</div>
              </div>
              <div className="flex items-center gap-2">
                {isDirty(g.group) && (
                  <Button size="sm" variant="ghost" onClick={() => revert(g.group)} data-testid={`revert-${g.group}`}>
                    <ArrowCounterClockwise size={13} className="mr-1" /> Revert
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={() => save(g.group)}
                  disabled={busy === g.group || !isDirty(g.group)}
                  data-testid={`save-${g.group}`}
                  className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 disabled:opacity-50"
                >
                  <FloppyDisk size={13} className="mr-1" /> {busy === g.group ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              {(drafts[g.group] || []).map((v, i) => (
                <div key={i} className="flex items-center gap-2 group" data-testid={`filter-row-${g.group}-${i}`}>
                  <div className="flex flex-col text-muted-foreground opacity-0 group-hover:opacity-70 transition-opacity">
                    <button onClick={() => moveValue(g.group, i, i - 1)} className="h-3 leading-none hover:text-foreground" title="Move up">▲</button>
                    <button onClick={() => moveValue(g.group, i, i + 1)} className="h-3 leading-none hover:text-foreground" title="Move down">▼</button>
                  </div>
                  <DotsSixVertical size={14} className="text-muted-foreground/40 shrink-0" />
                  <Input
                    value={v}
                    onChange={(e) => setValue(g.group, i, e.target.value)}
                    className="flex-1 h-9"
                    data-testid={`filter-input-${g.group}-${i}`}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-muted-foreground hover:text-red-600"
                    onClick={() => removeValue(g.group, i)}
                    data-testid={`filter-remove-${g.group}-${i}`}
                    title="Remove"
                  >
                    <X size={13} />
                  </Button>
                </div>
              ))}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => addValue(g.group)}
              className="mt-3 w-full border-dashed"
              data-testid={`filter-add-${g.group}`}
            >
              <Plus size={13} className="mr-1" /> Add value
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
